import React, { useState, useEffect, useRef, Fragment } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BsCheckLg, BsCheckAll } from "react-icons/bs";
import { Menu, Transition } from "@headlessui/react";
import { FaEllipsisV, FaPaperPlane, FaTrash } from "react-icons/fa";
import { FiMessageSquare } from "react-icons/fi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

import api from "@/services/api";
import useStore from "@/services/store";
import Button from "@/components/loading-button";
import Loader from "@/components/loader";
import i18n from "@/locales";

const translations = {
  en: {
    send_error: "Error sending message",
    last_active: "Last active: {{date}}",
    online: "Online",
    write_message: "Write your message...",
    send: "Send",
    delete: "Delete",
    today: "Today",
    yesterday: "Yesterday",
    no_conversation: "Select a conversation to start chatting",
  },
  fr: {
    send_error: "Erreur lors de l'envoi du message",
    last_active: "Dernière activité : {{date}}",
    online: "En ligne",
    write_message: "Écrivez votre message...",
    send: "Envoyer",
    delete: "Supprimer",
    today: "Aujourd'hui",
    yesterday: "Hier",
    no_conversation: "Sélectionnez une conversation pour commencer",
  },
};

const ChatConversation = ({ basePath = "/chat" }) => {
  const { t } = useComponentTranslation(translations);
  const { id } = useParams();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const messagesEndRef = useRef();

  const navigate = useNavigate();
  const { user } = useStore();

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (id) {
      fetchConversation();
      fetchMessages();
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (conversation?.job_id) {
      const fetchJob = async () => {
        try {
          const { ok, data } = await api.get(`/job/${conversation.job_id}`);
          if (ok) setJob(data);
        } catch (e) {
          console.error("Error fetching job:", e);
        }
      };
      fetchJob();
    }
  }, [conversation?.job_id]);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.get(`/conversation/${id}`);
      if (!ok) throw new Error("Conversation not found");
      setConversation(data);

      // Mark as read
      await api.put(`/conversation/${id}/read`);
    } catch (e) {
      console.error("Error fetching conversation:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const { ok, data } = await api.post("/message/search", { conversation_id: id });
      if (ok) setMessages(data);
    } catch (e) {
      console.error("Error fetching messages:", e);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;
    try {
      setIsSending(true);
      const { ok, data, code } = await api.post("/message", {
        conversation_id: id,
        content: newMessage,
        type: "text",
      });
      if (!ok) throw new Error(code);
      setNewMessage("");
      setMessages((prev) => [...prev, data]);
    } catch (e) {
      console.error("Error sending message:", e);
      toast.error(t("send_error"));
    } finally {
      setIsSending(false);
    }
  };

  const groupMessagesByDate = (msgs = []) => {
    const groups = {};
    msgs.forEach((message) => {
      const date = new Date(message.created_at || message.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  // Get the other participant (not the current user)
  const getOtherParticipant = () => {
    return conversation?.participants?.find((p) => p.user_id?.toString() !== user?._id?.toString()) || conversation?.participants?.[0];
  };

  const messageGroups = groupMessagesByDate(messages);
  const otherParticipant = getOtherParticipant();

  if (!id) {
    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl items-center justify-center text-gray-500">
        <FiMessageSquare className="w-12 h-12 mb-4 text-gray-400" />
        <p className="text-sm">{t("no_conversation")}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      {conversation && (
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-medium text-sm shrink-0">
              {otherParticipant?.user_avatar ? (
                <img src={otherParticipant.user_avatar} alt={otherParticipant?.user_name} className="w-full h-full rounded-full object-cover" />
              ) : conversation?.contact?.avatar ? (
                <img src={conversation.contact.avatar} alt={conversation.contact.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                otherParticipant?.user_name?.[0] || conversation?.contact?.name?.[0] || "?"
              )}
            </div>
            <div className="min-w-0">
              <h2 className="font-medium text-gray-900 truncate">{otherParticipant?.user_name || conversation?.contact?.name || "Unknown"}</h2>
              {conversation.job_title && <p className="text-sm text-gray-500">{conversation.job_title}</p>}
            </div>
          </div>
          <Actions conversation={conversation} basePath={basePath} />
        </div>
      )}

      {job && (
        <Link to={`/recruiter/job-ads/${job._id}/candidates`} className="px-4 py-2 md:p-6 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <h2 className="font-medium text-gray-900 truncate">{job.title}</h2>
          <p className="text-sm text-gray-500">{job.organization_name}</p>
        </Link>
      )}

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {Object.entries(messageGroups).map(([date, msgs]) => (
          <div key={date} className="mb-6">
            <div className="text-center mb-4">
              <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full inline-block">
                <MessageDate date={date} />
              </span>
            </div>
            {msgs.map((message) => {
              const isCurrentUser = (message.sender_id?.toString() || message.sender?.id) === user._id?.toString();
              return (
                <div key={message._id} className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-4`}>
                  <div className={`max-w-[70%] rounded-lg p-3 ${isCurrentUser ? "bg-primary text-white" : "bg-gray-100 text-gray-900"}`}>
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                    <div className={`flex items-center gap-1 mt-1 ${isCurrentUser ? "justify-end" : "justify-start"}`}>
                      <span className={`text-xs ${isCurrentUser ? "text-white/75" : "text-gray-500"}`}>
                        {new Date(message.created_at || message.timestamp).toLocaleTimeString(i18n.language || "en", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      {isCurrentUser && <MessageStatus status={message.status} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {conversation && (
        <form onSubmit={handleSendMessage} className="p-4 md:p-6 border-t border-gray-200">
          <div className="flex items-end gap-2">
            <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={t("write_message")} className="input flex-1" disabled={isSending} />
            <Button type="submit" isLoading={isSending} className="btn btn-primary">
              {t("send")}
              <FaPaperPlane className="w-4 h-4" />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChatConversation;

const Actions = ({ conversation, basePath = "/chat" }) => {
  const navigate = useNavigate();
  const { t } = useComponentTranslation(translations);

  const deleteConversation = async () => {
    try {
      const response = await api.remove(`/conversation/${conversation._id}`);
      if (!response.ok) throw response;
      navigate(basePath);
    } catch (e) {
      console.error("Error deleting conversation:", e);
    }
  };

  return (
    <div className="relative">
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
          <FaEllipsisV className="h-4 w-4" />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-lg shadow-lg z-10 border border-gray-200 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center`}
                    onClick={async () => await deleteConversation()}
                  >
                    <FaTrash className="h-4 w-4 mr-2" />
                    {t("delete")}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

const MessageStatus = ({ status }) => {
  switch (status) {
    case "sent":
      return <BsCheckLg className="text-white/60" />;
    case "delivered":
      return <BsCheckAll className="text-white/60" />;
    case "read":
      return <BsCheckAll className="text-white" />;
    default:
      return null;
  }
};

const MessageDate = ({ date }) => {
  const { t } = useComponentTranslation(translations);
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) {
    return t("today");
  } else if (messageDate.toDateString() === yesterday.toDateString()) {
    return t("yesterday");
  } else {
    return messageDate.toLocaleDateString(i18n.language || "en", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
};
